import { 
  IMainDetailsForm,
  IProductSpecificationInformationForm,
  IProductRelatedInformationForm,
  IVendorRelatedInformation,
  IVendorFacilityInformation 
} from "@/types/active-projects/MainDetails.types";

// Initial values for Product Specification Information Form
export const ProductSpecificationInformationFormInitial: IProductSpecificationInformationForm = {
  specificationVersion: "",
  versionNumber: "1",
  vendorNumber: "",
  productSpecificationType: "",
  productSpecificationName: "",
  artworkDeadline: "",
  artworkProjectId: "",
  opportunityBuy: "",
};

// Initial values for Product Related Information Form
export const ProductRelatedInformationFormInitial: IProductRelatedInformationForm = {
  sku: "",
  productName: "",
  legacyIncomplete: "no",
  isMultiPackProduct: "",
  numberOfFlavorsVarieties: "",  // Number of Flavors/Varieties in Pack
  weightVolumePackQty: "",
  weightUOM: "",
  brandType: "",
  category: "",
  categoryManager: "",
  buyer: "",
  trafficManager: "",
  qaComplianceManager: "",
  productCreator: "",            // Product Developer
  nutritionist: "",              // Nutritionist
  regulatoryAgenciesOversight: [],  // multi-select: array of selected values
  foodSaftyHaccpPlan: "",        // Moved from VendorFacilityInformation
  ftlList: "",                   // Product on FLT List
  warehousesWhereSupplied: "",   // Moved from VendorFacilityInformation
  projectType: "",               // New field: Project Type
  launchDate: "",                // New field: Launch Date
  regulatoryGovernance: "",      // New field: Regulatory Governance
  nationalDrugCodeNumber: "",   // New field: National Drug Code Number (HABA only)
  certifiedOrganic: "",       // Produce RAC only - Yes / No
  retailPrice: "",
  productSize: "",
  casePack: "",
};

// Initial values for Vendor Related Information
export const VendorRelatedInformationInitial: IVendorRelatedInformation = {
  vendorNumber: "",
  vendorName: "",
  sites: "",
};

// Initial values for Vendor Facility Information
export const VendorFacilityInformationInitial: IVendorFacilityInformation = {
  siteCode: "",
  siteName: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  country: "",
  zipCodePostalCode: "",
};

// Consolidated MainDetails Form Initial Values
export const MainDetailsFormInitial: IMainDetailsForm = {
  productSpecificationInformation: ProductSpecificationInformationFormInitial,
  productRelatedInformation: ProductRelatedInformationFormInitial,
  vendorRelatedInformation: VendorRelatedInformationInitial,
  vendorFacilityInformation: [VendorFacilityInformationInitial],  // Changed to array
  photosofItemInRetailPackaging: {},  // File uploads are handled separately in FormContext
  revisionNotes: "",  // Notes for revision requests
};

// Helper function to reset a specific section
export const getInitialSectionData = (section: keyof IMainDetailsForm) => {
  switch (section) {
    case 'productSpecificationInformation':
      return ProductSpecificationInformationFormInitial;
    case 'productRelatedInformation':
      return ProductRelatedInformationFormInitial;
    case 'vendorRelatedInformation':
      return VendorRelatedInformationInitial;
    case 'vendorFacilityInformation':
      return [VendorFacilityInformationInitial];
    default:
      throw new Error(`Unknown section: ${section}`);
  }
};

// Helper function to create empty form data
export const createEmptyMainDetailsForm = (): IMainDetailsForm => {
  return {
    productSpecificationInformation: { ...ProductSpecificationInformationFormInitial },
    productRelatedInformation: { ...ProductRelatedInformationFormInitial },
    vendorRelatedInformation: { ...VendorRelatedInformationInitial },
    vendorFacilityInformation: [{ ...VendorFacilityInformationInitial }],  // Changed to array
    photosofItemInRetailPackaging: {},  // File uploads are handled separately in FormContext
    revisionNotes: "",  // Notes for revision requests
  };
};

// Helper function to validate if form has data
export const hasFormData = (form: IMainDetailsForm): boolean => {
  const checkSection = (obj: any, initial: any): boolean => {
    return Object.keys(obj).some(key => obj[key] !== initial[key] && obj[key] !== "");
  };

  const hasVendorFacilityData = Array.isArray(form.vendorFacilityInformation) && 
    form.vendorFacilityInformation.some(facility => checkSection(facility, VendorFacilityInformationInitial));

  return (
    checkSection(form.productSpecificationInformation, ProductSpecificationInformationFormInitial) ||
    checkSection(form.productRelatedInformation, ProductRelatedInformationFormInitial) ||
    checkSection(form.vendorRelatedInformation, VendorRelatedInformationInitial) ||
    hasVendorFacilityData
  );
};

// Helper function to get form completion percentage
export const getFormCompletionPercentage = (form: IMainDetailsForm): number => {
  let totalFields = 0;
  let filledFields = 0;

  const countFields = (obj: any) => {
    Object.values(obj).forEach(value => {
      totalFields++;
      if (value && value !== "") {
        filledFields++;
      }
    });
  };

  countFields(form.productSpecificationInformation);
  countFields(form.productRelatedInformation);
  countFields(form.vendorRelatedInformation);
  
  // Handle vendor facility information array
  if (Array.isArray(form.vendorFacilityInformation)) {
    form.vendorFacilityInformation.forEach(facility => countFields(facility));
  }

  return totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
};
